// Révision du loyer (F-057, INT-18) : indices sourcés (valeurs fictives signalées), calcul tracé, gel F/G,
// pas de rétroactivité, courrier et historique des loyers. Aucune hausse n'est appliquée sans votre validation.
import { useState } from "react";
import { del, post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money, today } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, KeyValues, Loading, Notice, PageHeader, Select, SimBadge, Table, fieldError } from "@core/ui";
import { Amount, DocLink, Fictitious, useAction, useG } from "./shared";
import { parseIndex } from "./util";

type IndexRow = { id: string; kind: string; year: number; quarter: number; value_hundredths: number; source: string; fictitious: boolean; source_note: string; published_on: string | null; label: string; display: string };
type Indices = { items: IndexRow[]; kinds: { value: string; label: string }[]; provider: { name: string; mode: string }; rule: { version: string; source: string; url: string }; official_url: string };

const SOURCES: Record<string, string> = { saisie: "Saisie", insee_simule: "Simulateur Insee", demonstration: "Démonstration", insee: "Insee" };

export function RevisionsPage() {
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Révision des loyers"
        intro="Le nouveau loyer se calcule avec l’indice du même trimestre que l’indice de référence du bail : loyer × nouvel indice ÷ indice de référence, arrondi au centime. Vous validez avant toute application ; aucune révision n’est rétroactive."
      />
      <IndicesCard />
      <RevisionPanel />
    </div>
  );
}

export function IndicesCard({ orgId }: { orgId?: string }) {
  const { g, can } = useG(orgId);
  const q = useGet<Indices>(g("/indices"));
  const { busy, run, error } = useAction(orgId);
  const [f, setF] = useState({ kind: "IRL", quarter: "", value: "", source_note: "", published_on: "" });
  const canWrite = can("operate") || can("finance");
  const d = q.data;
  return (
    <Card
      title={
        <>
          Indices de révision {d ? <SimBadge mode={d.provider.mode} /> : null}
        </>
      }
    >
      <Notice tone="attention">
        Bailivo n’inscrit aucune valeur officielle d’indice. Saisissez la valeur publiée par l’Insee avec sa source{d ? <> (<a href={d.official_url} target="_blank" rel="noreferrer">Insee</a>)</> : null}. Le simulateur fournit uniquement des valeurs fictives, signalées comme telles.
      </Notice>
      {q.isLoading ? <Loading /> : null}
      {d && d.items.length === 0 ? <Empty title="Aucun indice enregistré" /> : null}
      {d && d.items.length ? (
        <Table caption="Indices enregistrés">
          <thead>
            <tr>
              <th>Indice</th>
              <th>Trimestre</th>
              <th className="right">Valeur</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.items.map((i) => (
              <tr key={i.id}>
                <td>{i.kind}</td>
                <td>{i.label}</td>
                <td className="right mono">{i.display}</td>
                <td>
                  {SOURCES[i.source] ?? i.source} {i.fictitious ? <Fictitious /> : null}
                  <span className="block small muted">{i.source_note}</span>
                </td>
                <td className="right">
                  {canWrite ? (
                    <Button variant="discret" small busy={busy === i.id} onClick={() => run(i.id, () => del(g(`/indices/${i.id}`)), "Indice supprimé.")}>
                      Supprimer
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {canWrite && d ? (
        <div className="stack-sm">
          <div className="form-row">
            <Select label="Indice" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={d.kinds} />
            <Input label="Trimestre" placeholder="2026-T2" value={f.quarter} onChange={(e) => setF({ ...f, quarter: e.target.value })} error={fieldError(error, "quarter")} />
            <Input label="Valeur publiée" placeholder="142,06" inputMode="decimal" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} error={fieldError(error, "value_hundredths")} />
          </div>
          <div className="form-row">
            <Input label="Source" placeholder="Insee, Informations rapides du …" value={f.source_note} onChange={(e) => setF({ ...f, source_note: e.target.value })} error={fieldError(error, "source_note")} />
            <Input label="Date de publication" type="date" value={f.published_on} onChange={(e) => setF({ ...f, published_on: e.target.value })} />
          </div>
          <div className="gestion-actions">
            <Button
              busy={busy === "add"}
              onClick={() => run("add", () => post(g("/indices"), { kind: f.kind, quarter: f.quarter, value_hundredths: parseIndex(f.value) ?? 0, source_note: f.source_note, published_on: f.published_on || undefined }), "Indice enregistré.")}
            >
              Enregistrer l’indice
            </Button>
            <Button variant="secondaire" busy={busy === "fetch"} disabled={!f.quarter} onClick={() => run("fetch", () => post<any>(g("/indices/fetch"), { kind: f.kind, quarter: f.quarter }), (r: any) => (r.fictitious ? "Valeur FICTIVE du simulateur enregistrée : ne l’utilisez pas pour un bail réel." : "Valeur obtenue."))}>
              Obtenir via l’adaptateur Insee (simulateur)
            </Button>
          </div>
        </div>
      ) : null}
      {d ? (
        <p className="small muted">
          Règle de gel : {d.rule.version}. {d.rule.source}{" "}
          <a href={d.rule.url} target="_blank" rel="noreferrer">
            Source
          </a>
        </p>
      ) : null}
    </Card>
  );
}

type Upcoming = { lease_id: string; property_label: string; anniversary: string; regime: string; rent_cents: number; dpe_class: string | null; index_kind: string; reference_quarter: string | null; suggested_new_quarter: string | null; has_clause: boolean; frozen_class: boolean };
type RevisionRow = {
  id: string;
  lease_id: string;
  index_kind: string;
  reference_label: string;
  new_label: string;
  old_rent_cents: number;
  computed_rent_cents: number;
  new_rent_cents: number;
  effective_on: string;
  frozen: boolean;
  fictitious_indices: boolean;
  rule_version: string;
  explanation: string[];
  status: string;
  letter_document_id: string | null;
  created_at: string;
  property_label: string;
};

export function RevisionPanel({ leaseId, orgId }: { leaseId?: string; orgId?: string }) {
  const { g } = useG(orgId);
  const q = useGet<{ items: RevisionRow[]; leases: Upcoming[] }>(g(`/revisions${leaseId ? `?lease_id=${leaseId}` : ""}`));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  return (
    <>
      <Card title="Baux à réviser">
        {d.leases.length === 0 ? <Empty title="Aucun bail actif" /> : null}
        <ul className="gestion-list">
          {d.leases.map((l) => (
            <li key={l.lease_id}>
              <RevisionForm lease={l} orgId={orgId} />
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Historique des révisions">
        {d.items.length === 0 ? <p className="muted">Aucune révision.</p> : null}
        <ul className="gestion-list">
          {d.items.map((r) => (
            <RevisionItem key={r.id} r={r} orgId={orgId} />
          ))}
        </ul>
      </Card>
    </>
  );
}

function RevisionForm({ lease, orgId }: { lease: Upcoming; orgId?: string }) {
  const { g, can } = useG(orgId);
  const { busy, run } = useAction(orgId);
  const [f, setF] = useState({ reference_quarter: lease.reference_quarter ?? "", new_quarter: lease.suggested_new_quarter ?? "", effective_on: lease.anniversary >= today() ? lease.anniversary : today(), clause_confirmed: lease.has_clause, dpe_confirmed: false });
  const [preview, setPreview] = useState<any>(null);
  const body = { lease_id: lease.lease_id, index_kind: lease.index_kind, ...f };
  return (
    <div className="stack-sm">
      <div className="gestion-line">
        <span>
          <strong>{lease.property_label}</strong> · loyer {money(lease.rent_cents)} · date anniversaire {date(lease.anniversary)} · indice {lease.index_kind}
          <span className="block small muted">
            DPE {lease.dpe_class ?? "non renseigné"}
            {lease.frozen_class ? " : hausse gelée (classe F ou G)" : ""}
          </span>
        </span>
        {lease.frozen_class ? <Badge tone="attention">Gel du loyer</Badge> : null}
      </div>
      {can("operate") ? (
        <>
          <div className="form-row">
            <Input label="Trimestre de référence" placeholder="2025-T2" value={f.reference_quarter} onChange={(e) => setF({ ...f, reference_quarter: e.target.value })} />
            <Input label="Nouveau trimestre" placeholder="2026-T2" value={f.new_quarter} onChange={(e) => setF({ ...f, new_quarter: e.target.value })} />
            <Input label="Date d’effet" type="date" min={today()} value={f.effective_on} onChange={(e) => setF({ ...f, effective_on: e.target.value })} />
          </div>
          <Checkbox label="Le bail contient une clause de révision annuelle" checked={f.clause_confirmed} onChange={(v) => setF({ ...f, clause_confirmed: v })} />
          {!lease.dpe_class ? <Checkbox label="Je confirme que le logement n’est classé ni F ni G au DPE" checked={f.dpe_confirmed} onChange={(v) => setF({ ...f, dpe_confirmed: v })} /> : null}
          <div className="gestion-actions">
            <Button variant="secondaire" small busy={busy === "preview"} disabled={!f.reference_quarter || !f.new_quarter} onClick={async () => setPreview(await run("preview", () => post(g("/revisions/preview"), body)))}>
              Calculer
            </Button>
            {preview && preview.blockers.length === 0 ? (
              <Button small busy={busy === "create"} onClick={async () => { const r = await run("create", () => post(g("/revisions"), body), "Proposition de révision enregistrée : validez-la pour l’appliquer."); if (r) setPreview(null); }}>
                Enregistrer la proposition
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      {preview ? (
        <div className="gestion-proposal stack-sm">
          <KeyValues
            items={[
              ["Indice de référence", `${preview.reference.label} : ${preview.reference.display}`],
              ["Nouvel indice", `${preview.new.label} : ${preview.new.display}`],
              ["Loyer actuel", money(preview.current_rent_cents)],
              ["Résultat du calcul", money(preview.outcome.computed_rent_cents)],
              ["Loyer proposé", <strong key="n">{money(preview.outcome.new_rent_cents)}</strong>],
            ]}
          />
          <ul className="small">
            {preview.outcome.explanation.map((e: string, i: number) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
          {preview.blockers.map((b: string, i: number) => (
            <Notice key={`b${i}`} tone="erreur">
              {b}
            </Notice>
          ))}
          {preview.warnings.map((w: string, i: number) => (
            <Notice key={`w${i}`} tone="attention">
              {w}
            </Notice>
          ))}
          <p className="small muted">{preview.rounding}</p>
        </div>
      ) : null}
    </div>
  );
}

function RevisionItem({ r, orgId }: { r: RevisionRow; orgId?: string }) {
  const { g, can } = useG(orgId);
  const { busy, run } = useAction(orgId);
  const [ack, setAck] = useState(false);
  return (
    <li>
      <div className="gestion-line">
        <span>
          <strong>{r.property_label}</strong> · {r.index_kind} {r.reference_label} → {r.new_label} · effet le {date(r.effective_on)}
          <span className="block small">
            {money(r.old_rent_cents)} → <Amount cents={r.new_rent_cents} strong /> {r.frozen ? <Badge tone="attention">Hausse gelée (F ou G)</Badge> : null} {r.fictitious_indices ? <Fictitious /> : null}
          </span>
          <span className="block small muted">
            {r.status === "appliquee" ? "Appliquée" : r.status === "abandonnee" ? "Abandonnée" : "Proposée, à valider"} · {dateTime(r.created_at)} · règle {r.rule_version}
          </span>
        </span>
        <DocLink id={r.letter_document_id}>Courrier de révision</DocLink>
      </div>
      {r.status === "proposee" && can("operate") ? (
        <div className="gestion-actions">
          {r.fictitious_indices ? <Checkbox label="J’ai compris que les indices sont fictifs (démonstration)" checked={ack} onChange={setAck} /> : null}
          <Button small busy={busy === "apply"} disabled={r.fictitious_indices && !ack} onClick={() => run("apply", () => post(g(`/revisions/${r.id}/apply`), { acknowledge_fictitious: ack }), "Révision appliquée : échéances futures mises à jour, courrier prêt dans « Courriers ».")}>
            Valider et appliquer
          </Button>
          <Button variant="discret" small busy={busy === "abandon"} onClick={() => run("abandon", () => post(g(`/revisions/${r.id}/abandon`)), "Révision abandonnée.")}>
            Abandonner
          </Button>
        </div>
      ) : null}
    </li>
  );
}
